import {
  isComponentBlock,
  isInstanceBlock,
  isSelectBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { WriteTransaction } from 'replicache'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { updateBlock } from './crud'
import { sortSelectBlockAcceptedValues } from '@basehub/data/replicache/schema/block/select-utils'
import { BlockBatchMutationParams } from './batch'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { idToSourceBlockId } from './copy-on-write'

export type SelectOptionOpParams = {
  path: string
  id: string
  isoNow: string
  data: {
    op: 'rename' | 'delete'
    index: number
    value: string
  }
}

export const selectOptionOp = async (
  tx: WriteTransaction,
  { path, id, data, isoNow }: WithServerSecret<SelectOptionOpParams>
) => {
  ensureBaseHubTransaction(tx)

  const normalizedPath = normalizeStringPath({
    path,
    edgeId: id,
    format: 'array',
  })

  const rootId = normalizedPath[0]
  invariant(rootId, `Root block not found`)
  const parentId = normalizedPath[normalizedPath.length - 2]

  const [block, parent] = await Promise.all([
    blockBaseOps.get(tx, id),
    parentId ? blockBaseOps.get(tx, parentId) : null,
  ])
  invariant(block, `Block not found`)
  invariant(isSelectBlock(block), `Block is not a select block`)
  invariant(parent, `Parent block not found`)

  let oldValue = ''
  const updatedAcceptedValues = sortSelectBlockAcceptedValues(
    block.constraints.acceptedValues
  )
    .map((option, index) => {
      if (index === data.index) {
        oldValue = option
        switch (data.op) {
          case 'delete':
            return null
          case 'rename':
            return data.value
        }
      }
      return option
    })
    .filter(Boolean)
  const updatedValue = block.value
    .map((value) => {
      if (value === oldValue) {
        switch (data.op) {
          case 'delete':
            return null
          case 'rename':
            return data.value
        }
      }
      return value
    })
    .filter(Boolean)

  const undo: BlockBatchMutationParams['mutations'] = []

  if (isComponentBlock(parent)) {
    // update select blocks in all instances of the component so the value is kept in sync
    const tree = await tx.getTreeManager(rootId)
    const matches = (
      (tree.compoundIndexes.get('byInstanceChildTarget') ?? new Map()) as Map<
        string,
        Set<string>
      >
    ).get(idToSourceBlockId(block))
    if (matches && matches.size > 0) {
      for (const matchId of matches) {
        const selectBlockInInstance = tree.getNode(matchId, {
          required: true,
        })
        invariant(
          isSelectBlock(selectBlockInInstance.value),
          `expected match to be a select block`
        )
        const updatedValue = selectBlockInInstance.value.value
          .map((value) => {
            if (value === oldValue) {
              switch (data.op) {
                case 'delete':
                  return null
                case 'rename':
                  return data.value
              }
            }
            return value
          })
          .filter(Boolean)
        const { undo: undoInstance } = await updateBlock(tx, {
          id: matchId,
          instanceChildTarget: idToSourceBlockId(block),
          data: {
            type: 'select',
            value: updatedValue,
          },
          isoNow,
          path: tree.getPath(matchId, {
            required: true,
            format: 'string',
          }),
          strategy: 'merge',
        })
        undo.push(...undoInstance)
      }
    }
  } else if (isInstanceBlock(parent)) {
    throw new MutatorUserError(`Cannot rename select option in instance block`)
  } else {
    // all good, just update it
  }

  const { undo: undoUpdate, ...rest } = await updateBlock(tx, {
    instanceChildTarget: block.instanceChildTarget,
    data: {
      type: 'select',
      constraints: {
        acceptedValues: updatedAcceptedValues,
      },
      value: updatedValue,
    },
    strategy: 'merge-deep',
    id,
    isoNow,
    path,
  })

  undo.push(...undoUpdate)

  return { undo, ...rest }
}
